import { Fragment, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isWeaveUser as getIsWeaveUser, onWeaveTokenChange } from '@frontend/auth-helpers';
import { getUserChannel, setUserChannel, usePollForUpdates, type Channel } from '@frontend/automatic-updates';
import appConfig from '@frontend/env';
import { sentry } from '@frontend/tracking';
import { siteChannels } from '../constants/constants';
import { useTabManagement } from './tab-management';

// Event name constant for Sherlock devtools refresh events
const SHERLOCK_REFRESH_EVENT = 'sherlock:app-refresh';

type RefreshEventDetail = {
  type: 'hard' | 'soft';
  reason?: string;
};

type ContextValue = {
  currentVersion: string | undefined;
  updateAvailable: boolean;
  shouldPreventUpdate: boolean;
  setShouldPreventUpdate: (value: boolean) => void;
  refresh: (type: 'hard' | 'soft', reason?: string) => void;
  hasAlreadyRefreshed: (reason?: string) => boolean;
} & ReturnType<typeof useChannelAssignment>;

const AppContext = createContext<ContextValue>({} as ContextValue);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  useTabManagement();

  const { currentChannel, setCurrentChannel } = useChannelAssignment();
  const [shouldPreventUpdate, setShouldPreventUpdate] = useState(false);
  const url = appConfig.PUBLISH_URL ? 'https://' + appConfig.PUBLISH_URL + '/_m/version' : undefined;

  const { updateAvailable, currentVersion } = usePollForUpdates(60000, url);

  useEffect(() => {
    if (currentVersion) {
      localStorage.setItem('current-version', currentVersion);
    } else {
      localStorage.removeItem('current-version');
    }
  }, [currentVersion]);

  const [key, setKey] = useState(Math.random());

  // Allows resetting the app to force a remount of the entire provider tree and components.
  // Prevents refresh loops by tracking local storage keys with a one minute expiry.
  const refresh = useCallback((type: 'hard' | 'soft', reason?: string) => {
    if (!reason) {
      reason = 'unknown';
    }
    const now = Date.now();
    const keyName = `last-refresh-reason-${reason}`;

    if (!hasAlreadyRefreshed(reason)) {
      localStorage.setItem(keyName, JSON.stringify({ expiry: now }));
      if (type === 'hard') {
        sentry.error({
          error: `page refresh: ${reason}`,
          topic: 'infra',
          team: 'frontend-ops',
          severityLevel: 'error',
          addContext: {
            name: 'reason',
            context: {
              windowPathname: window.location.pathname,
              reason,
            },
          },
        });
        location.reload();
      } else {
        // just updates the key to trigger a remount of the app full provider tree
        setKey(Math.random());
      }
    } else {
      sentry.error({
        error: `page refresh loop attempted: ${reason}`,
        topic: 'infra',
        team: 'frontend-ops',
        severityLevel: 'error',
        addContext: {
          name: 'reason',
          context: {
            windowPathname: window.location.pathname,
            reason,
          },
        },
      });
    }
    removeExpiredRefreshReasons(reason);
  }, []);

  useEffect(() => {
    removeExpiredRefreshReasons();
  }, []);

  // Listen for refresh events from Sherlock devtools
  useEffect(() => {
    const handleSherlockRefresh = (event: Event) => {
      const customEvent = event as CustomEvent<RefreshEventDetail>;
      const { type, reason } = customEvent.detail;
      refresh(type, reason);
    };

    window.addEventListener(SHERLOCK_REFRESH_EVENT, handleSherlockRefresh);
    return () => {
      window.removeEventListener(SHERLOCK_REFRESH_EVENT, handleSherlockRefresh);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      currentVersion,
      updateAvailable,
      shouldPreventUpdate,
      setShouldPreventUpdate,
      refresh,
      hasAlreadyRefreshed: (reason?: string) => hasAlreadyRefreshed(reason),
      currentChannel,
      setCurrentChannel,
    }),
    [
      currentVersion,
      updateAvailable,
      refresh,
      shouldPreventUpdate,
      setShouldPreventUpdate,
      currentChannel,
      setCurrentChannel,
    ]
  );
  return (
    <AppContext.Provider value={value}>
      <Fragment key={key}>{children}</Fragment>
    </AppContext.Provider>
  );
};

export const useApp = () => {
  return useContext(AppContext);
};

const getExpiry = (item: string | null) => {
  if (!item) return undefined;
  try {
    return (JSON.parse(item) as { expiry: number }).expiry;
  } catch {
    return undefined;
  }
};

// Checks if a refresh has already occurred for a given reason within the last minute
const hasAlreadyRefreshed = (reason?: string) => {
  if (!reason) reason = 'unknown';
  const keyName = `last-refresh-reason-${reason}`;
  const now = Date.now();

  const expiry = getExpiry(localStorage.getItem(keyName));
  return !!expiry && now - expiry < 60 * 1000;
};

const removeExpiredRefreshReasons = (exclude?: string) => {
  const now = Date.now();
  // Remove any refresh reasons with an expiry over one minute
  Object.keys(localStorage)
    .filter((k) => k.startsWith('last-refresh-reason-') && (exclude ? !k.endsWith(exclude) : true))
    .forEach((k) => {
      const expiry = getExpiry(localStorage.getItem(k));
      if (!expiry || now - expiry > 60 * 1000) {
        localStorage.removeItem(k);
      }
    });
};

const useChannelAssignment = () => {
  const [isWeaveUser, setIsWeaveUser] = useState(() => getIsWeaveUser());
  const [currentChannel, setCurrentChannel] = useState<Channel | undefined>(() =>
    getUserChannel(isWeaveUser, siteChannels)
  );

  useEffect(() => {
    const handleUserChange = () => {
      setIsWeaveUser(getIsWeaveUser());
    };
    const unsubscribe = onWeaveTokenChange(handleUserChange);
    return () => {
      unsubscribe();
    };
  }, [setIsWeaveUser, getIsWeaveUser]);

  useEffect(() => {
    setCurrentChannel(siteChannels ? getUserChannel(isWeaveUser, siteChannels) : undefined);
  }, [isWeaveUser, siteChannels]);

  const setChannel = useCallback(
    (channel: Channel) => {
      setUserChannel(channel);
      setCurrentChannel(channel);
    },
    [setUserChannel, setCurrentChannel]
  );

  return {
    currentChannel,
    setCurrentChannel: setChannel,
  };
};
